/**
 * Helpers shared by the Notice Preview editor.
 *
 * The generated notice .docx has no separate Word header part - the letterhead
 * (logo + centre address) is the first <w:tbl> inside document.xml and the body
 * is everything after it. The backend returns the whole notice as a single
 * `content_html` string with the letterhead inline, so the split happens here:
 * `splitLetterhead` peels the letterhead off, it is rendered read-only outside
 * the editor, and `joinNoticeHtml` re-attaches the pristine slice on save. The
 * letterhead therefore never passes through TinyMCE and cannot be restructured.
 */

import ACCBTheme from "Clients/ACCB/ACCBTheme";

/* ------------------------------------------------------------------ *
 * Sanitiser
 * ------------------------------------------------------------------ */

// Tags the notice body may legitimately contain. So anything
// the editor can produce is something the document can faithfully render.
const BODY_TAGS = [
  "P", "BR", "DIV", "SPAN",
  "STRONG", "B", "EM", "I", "U", "S", "SUB", "SUP",
  "UL", "OL", "LI",
  "A",
  "H3", "H4", "HR",
  "TABLE", "THEAD", "TBODY", "TFOOT", "TR", "TD", "TH", "CAPTION", "COL", "COLGROUP",
];

// Images stay header-only: the logo belongs to the letterhead, and the body
// converter embeds only data-URI images anyway.
const HEADER_EXTRA_TAGS = ["IMG"];

const ALLOWED_ATTRS = {
  A: ["href", "title", "target", "rel"],
  IMG: ["src", "alt", "width", "height"],
  TD: ["colspan", "rowspan", "style", "align", "valign", "width"],
  TH: ["colspan", "rowspan", "style", "align", "valign", "width"],
  TABLE: ["style", "align", "width", "border", "cellpadding", "cellspacing"],
  TR: ["style"],
  "*": ["style"],
};

// Only presentational properties the docx converter can map are kept.
//
// This list is half of a contract with bac/html_docx.py: that module writes a
// notice's paragraph formatting out as inline CSS and reads the same properties
// back when rebuilding the .docx. Anything dropped here is dropped from the
// printed notice — that is how edited notices lost their `Sub:`/`-reg.` indents
// and their page breaks. Keep in step with _paragraph_style_css /
// _apply_paragraph_style.
const ALLOWED_STYLE_PROPS = [
  "text-align", "font-weight", "font-style", "text-decoration",
  "font-family", "font-size", "color", "background-color",
  "margin", "margin-left", "margin-right", "margin-top", "margin-bottom",
  "padding", "padding-left", "padding-right", "padding-top", "padding-bottom",
  "width", "border", "border-bottom", "border-top", "line-height",
  "vertical-align", "list-style-type",
  // Paragraph geometry the .docx round trip depends on.
  // Both spellings - see PRESERVED_STYLE_PROPS in
  // Components/RichTextEditor/extensions/styleProps.js.
  "text-indent", "page-break-before", "page-break-after",
  "break-before", "break-after",
];

const SAFE_URL = /^(https?:|mailto:|tel:)/i;
const SAFE_IMG_SRC = /^(https?:|data:image\/)/i;

const cleanStyle = (value) =>
  String(value || "")
    .split(";")
    .map((decl) => decl.trim())
    .filter(Boolean)
    .filter((decl) => {
      const prop = decl.split(":")[0].trim().toLowerCase();
      if (!ALLOWED_STYLE_PROPS.includes(prop)) return false;
      // url()/expression() can smuggle in remote or executable content.
      return !/url\s*\(|expression\s*\(|javascript:/i.test(decl);
    })
    .join("; ");

/**
 * Allowlist sanitiser. Everything not explicitly permitted is dropped, with the
 * element's text content preserved so no wording is silently lost.
 *
 * @param {string} html
 * @param {{ allowImages?: boolean }} [options] header mode: additionally
 *   permits the letterhead image and its wrapping table.
 * @returns {string}
 */
export const sanitizeNoticeHtml = (html, options = {}) => {
  if (!html || typeof html !== "string") return "";

  const allowed = new Set(
    options.allowImages ? [...BODY_TAGS, ...HEADER_EXTRA_TAGS] : BODY_TAGS
  );

  let doc;
  try {
    doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  } catch (err) {
    return "";
  }

  const root = doc.body.firstElementChild;
  if (!root) return "";

  // script/style/iframe and friends are removed wholesale - their text content
  // is markup, not wording, so unwrapping them would be wrong.
  root
    .querySelectorAll("script, style, iframe, object, embed, link, meta, form, input, button")
    .forEach((node) => node.remove());

  const walk = (node) => {
    // Copy the list first: we mutate the tree while iterating.
    Array.from(node.children).forEach(walk);

    const tag = node.tagName;

    if (!allowed.has(tag)) {
      // Unwrap: keep the children/text, drop the element itself.
      const parent = node.parentNode;
      if (!parent) return;
      while (node.firstChild) parent.insertBefore(node.firstChild, node);
      parent.removeChild(node);
      return;
    }

    const permitted = [...(ALLOWED_ATTRS[tag] || []), ...ALLOWED_ATTRS["*"]];
    Array.from(node.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();

      if (name.startsWith("on") || !permitted.includes(name)) {
        node.removeAttribute(attr.name);
        return;
      }

      if (name === "style") {
        const cleaned = cleanStyle(attr.value);
        if (cleaned) node.setAttribute("style", cleaned);
        else node.removeAttribute("style");
        return;
      }

      if (name === "href" && !SAFE_URL.test(attr.value.trim())) {
        node.removeAttribute("href");
        return;
      }

      if (name === "src" && !SAFE_IMG_SRC.test(attr.value.trim())) {
        node.removeAttribute("src");
      }
    });

    if (tag === "A" && node.getAttribute("target") === "_blank") {
      node.setAttribute("rel", "noopener noreferrer");
    }
  };

  Array.from(root.children).forEach(walk);
  return root.innerHTML;
};

/* ------------------------------------------------------------------ *
 * Letterhead split
 * ------------------------------------------------------------------ */

// The letterhead table carries the logo and/or the name of the centre.
const LETTERHEAD_TEXT = /ARBITRATION|CONCILIATION\s+CENTRE/i;
// First line of the body proper on the generated notices.
const DISPATCH_MARKER = /Dispatch\s*No/i;

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

const isElement = (node) => node.nodeType === ELEMENT_NODE;

const carriesImage = (node) =>
  node.tagName === "IMG" || Boolean(node.querySelector("img"));

// Re-serialises a run of top-level nodes. Elements keep their markup verbatim
// via outerHTML; whitespace between blocks is preserved as text, so the two
// slices concatenate back into the document they came from.
const serializeNodes = (nodes) =>
  nodes
    .map((node) => {
      if (isElement(node)) return node.outerHTML;
      if (node.nodeType === TEXT_NODE) {
        return String(node.nodeValue || "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
      }
      return "";
    })
    .join("");

/**
 * Splits the single `content_html` the backend returns into the fixed
 * letterhead and the editable body.
 *
 * `headerHtml` is the UNSANITISED slice: it is what gets re-attached on save,
 * so it stays as close to what the backend produced as a DOM round-trip allows.
 * Sanitise a separate copy for display.
 *
 * Layered, so an unrecognised template variant degrades to "the whole notice is
 * editable" rather than to a blank editor.
 *
 * @param {string} contentHtml
 * @returns {{ headerHtml: string, bodyHtml: string }}
 */
export const splitLetterhead = (contentHtml) => {
  if (!contentHtml || typeof contentHtml !== "string") {
    return { headerHtml: "", bodyHtml: "" };
  }

  const fullyEditable = { headerHtml: "", bodyHtml: contentHtml };

  let root;
  try {
    const doc = new DOMParser().parseFromString(
      `<div>${contentHtml}</div>`,
      "text/html"
    );
    root = doc.body.firstElementChild;
  } catch (err) {
    return fullyEditable;
  }
  if (!root) return fullyEditable;

  const nodes = Array.from(root.childNodes);
  // Count of leading nodes making up the letterhead; -1 until located.
  let end = -1;

  // 1. The letterhead table itself, plus everything before it.
  const tableIndex = nodes.findIndex(
    (node) =>
      isElement(node) &&
      node.tagName === "TABLE" &&
      (Boolean(node.querySelector("img")) ||
        LETTERHEAD_TEXT.test(node.textContent || ""))
  );
  if (tableIndex !== -1) end = tableIndex + 1;

  // 2. Fallback: everything up to, but not including, the dispatch line.
  if (end === -1) {
    const dispatchIndex = nodes.findIndex(
      (node) => isElement(node) && DISPATCH_MARKER.test(node.textContent || "")
    );
    if (dispatchIndex > 0) end = dispatchIndex;
  }

  // 3. Fallback: the leading run of elements carrying the logo.
  if (end === -1) {
    let index = 0;
    let sawImage = false;
    while (index < nodes.length) {
      const node = nodes[index];
      if (isElement(node)) {
        if (!carriesImage(node)) break;
        sawImage = true;
      }
      index += 1;
    }
    if (sawImage) end = index;
  }

  // 4. Nothing matched - hand the whole notice to the editor.
  if (end === -1) {
    console.warn(
      "[notice] Could not locate the letterhead in content_html; the whole notice will be editable."
    );
    return fullyEditable;
  }

  const headerHtml = serializeNodes(nodes.slice(0, end));
  const bodyHtml = serializeNodes(nodes.slice(end));

  // A heuristic that swallowed the whole notice would leave nothing to edit,
  // which is worse than not splitting at all.
  if (isHtmlBlank(bodyHtml)) {
    console.warn(
      "[notice] Letterhead split left an empty body; falling back to a fully editable notice."
    );
    return fullyEditable;
  }

  return { headerHtml, bodyHtml };
};

/**
 * Re-attaches the pristine letterhead to the edited body. Trivial, but
 * centralised so the save path and the round-trip test agree on it.
 *
 * @param {string} headerHtml
 * @param {string} bodyHtml
 * @returns {string}
 */
export const joinNoticeHtml = (headerHtml, bodyHtml) =>
  `${headerHtml || ""}${bodyHtml || ""}`;

/* ------------------------------------------------------------------ *
 * Editor configuration
 * ------------------------------------------------------------------ */

// Centralized TinyMCE API key (resolves stage vs prod key dynamically)
export { TINYMCE_API_KEY } from "../../tinymceConfig";

// The read-only preview and the editor are two renderings of the same notice,
// so they share one description of how a notice body is laid out: this CSS is
// emitted into the editor iframe through content_style (scope "body") and into
// the preview through a scoped <style> tag (scope ".accb-notice-body"). Spacing
// added to one scope only is spacing that makes toggling Edit reflow the notice.
//
// Every length below is read off the notice template itself (word/styles.xml
// and word/document.xml of a generated notice), so the on-screen notice breaks
// its lines where the printed one does:
//
//   page          12240 x 15840 twips  -> Letter, 8.5in x 11in
//   margins       720 top/bottom, 864 left/right twips -> 0.5in / 0.6in
//   text column   8.5in - 2 x 0.6in    -> 7.3in
//   Normal style  Bookman Old Style, w:sz 24 half-points -> 12pt
//   body para     w:line 360 (1.5 lines), w:after 240 (12pt),
//                 w:ind firstLine 576 (0.4in), w:jc both
//   letterhead    Bookman Old Style 10pt, single spaced, 1.5pt rule under it
//   fee tables    w:jc center, w:tblBorders sz 6 (0.75pt), cell margin 108
//                 twips (0.075in), cell paragraphs w:before/after 40 (2pt)
//
// Units are pt/in so the browser does the same arithmetic Word does; rem is
// avoided because inside the editor iframe it resolves against the iframe's own
// root font size, not the app's.
export const NOTICE_BODY_CLASS = "accb-notice-body";

// Page geometry, used by NoticePreview to shape the sheet the notice sits on.
export const NOTICE_PAGE_WIDTH = "8.5in";
export const NOTICE_MARGIN_X = "0.6in";
export const NOTICE_MARGIN_TOP = "0.5in";
export const NOTICE_MARGIN_BOTTOM = "0.5in";

// Bookman Old Style ships with Office, so the admins editing these notices have
// it. The fallbacks are the closest widely available old-style serifs - never a
// sans, which would misrepresent the printed notice.
export const NOTICE_FONT_STACK =
  ACCBTheme.typography.fontFamily;
export const NOTICE_FONT_SIZE = "12pt";
// The letterhead runs are 10pt (the e-mail/website line 9pt).
export const NOTICE_LETTERHEAD_FONT_SIZE = "10pt";
// w:extent on the logo: 1260000 x 1112248 EMU. The image itself is 307px wide,
// so without this it renders more than twice the size Word prints it at.
export const NOTICE_LOGO_WIDTH = "1.378in";

// Word's "space after" for a body paragraph.
const PARA_SPACING = "12pt";
// Not a margin of its own: the first paragraph of the notice carries its own
// w:before (6pt on these templates), so this is only enough of a cushion that a
// caret parked on the first line is not clipped by the toolbar above it.
const BODY_SPACING_TOP = "2pt";

/**
 * The notice body stylesheet, scoped to a selector. The side padding is the
 * printed page margin, so both scopes lay the text out over the same 7.3in
 * column that Word does.
 *
 * @param {string} scope "body" inside the editor iframe, ".accb-notice-body"
 *   for the read-only preview.
 * @returns {string} CSS
 */
export const noticeBodyCss = (scope) => `
  ${scope} {
    font-family: ${NOTICE_FONT_STACK};
    font-size: ${NOTICE_FONT_SIZE};
    /* w:line 360 on the body paragraphs. Paragraphs the notice formats itself
       (the single-spaced address block) carry their own line-height. */
    line-height: 1.5;
    color: #000;
    margin: 0;
    padding: ${BODY_SPACING_TOP} ${NOTICE_MARGIN_X} ${NOTICE_MARGIN_BOTTOM};
  }
  ${scope} p { margin: 0 0 ${PARA_SPACING} 0; }
  ${scope} h3, ${scope} h4 {
    margin: 0 0 ${PARA_SPACING} 0;
    font-size: ${NOTICE_FONT_SIZE};
    line-height: 1.5;
  }
  ${scope} ul, ${scope} ol { margin: 0 0 ${PARA_SPACING} 0.4in; padding: 0; }
  ${scope} li { margin: 0; }
  ${scope} a { color: #0000ff; text-decoration: underline; }
  ${scope} table {
    border-collapse: collapse;
    max-width: 100%;
    margin: 0;
    width: auto;
    height: auto;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }
  ${scope} td, ${scope} th {
    border: 1px solid #000;
    padding: 0 0.075in;
    vertical-align: top;
  }
  /* Word gives the cell paragraphs the vertical breathing room, not the cell. */
  ${scope} td p, ${scope} th p { margin: 2pt 0; }
  /* No dangling gap under the last line of the notice. */
  ${scope} > p:last-child { margin-bottom: 0; }
  /* Page breaks are real in the printed notice (the Fee Schedule and the
     Instruction Note are separate pages) but invisible in a scrolling view,
     so mark them - otherwise an admin cannot tell where a page ends. */
  ${scope} p[style*="page-break-before"] {
    border-top: 1px dashed #b0b0b0;
    margin-top: 24pt;
    padding-top: 12pt;
  }
`;

// Restricted to what the backend converter (python-docx + BeautifulSoup) can
// represent in .docx: paragraphs, bold/italic/underline, alignment and lists.
//
// Deliberate inclusions, all present so that nothing already in the notice is
// destroyed on the way IN:
//   - a[href]: the notices carry real links (platform upload, payment, meeting).
//   - table and friends: the Fee Schedule, the respondent's share table, the
//     Instruction Note and the Sixth-Schedule disclosure form are all real
//     content tables in the body. Stripping them guts those notices.
//   - img: reachable when splitLetterhead found no letterhead and it is
//     therefore still sitting in the body.
export const NOTICE_VALID_ELEMENTS = [
  "p[style]",
  "br",
  "strong/b",
  "em/i",
  "u",
  "s",
  "span[style]",
  "ul",
  "ol",
  "li",
  "a[href|title|target|rel]",
  "table[style|border|width|cellpadding|cellspacing]",
  "thead",
  "tbody",
  "tfoot",
  "tr[style]",
  "td[style|colspan|rowspan|width|valign|align]",
  "th[style|colspan|rowspan|width|valign|align]",
  "img[src|alt|width|height]",
  "h3",
  "h4",
].join(",");

export const NOTICE_TOOLBAR =
  "undo redo | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist | outdent indent | link | table | removeformat";

export const buildNoticeEditorInit = () => ({
  // A floor, not the actual size: NoticePreview stretches the editor to fill
  // the dialog. The old fixed 460px box scrolled inside a dialog that was
  // already scrolling, so about a dozen lines of the notice were visible at a
  // time and the letterhead above it was pushed out of view.
  height: 560,
  menubar: false,
  statusbar: false,
  branding: false,
  // `link` is loaded so the anchors the notices actually carry (the platform
  // upload URL, the payment link, the meeting link) can be inspected and
  // corrected. Without it a link is inert in the editor - clicking just places
  // the caret. bac/html_docx.py renders <a href> as a real w:hyperlink either
  // way, so links stay clickable in the PDF.
  plugins: ["lists", "advlist", "wordcount", "searchreplace", "table", "paste", "autolink", "link"],
  toolbar: NOTICE_TOOLBAR,
  elementpath: false,
  forced_root_block: "p",
  valid_elements: NOTICE_VALID_ELEMENTS,
  // Must permit every property bac/html_docx.py round-trips, or TinyMCE strips
  // it on the way in and the rebuilt .docx loses it. See ALLOWED_STYLE_PROPS.
  valid_styles: {
    "*":
      "text-align,text-indent,line-height,font-size,font-weight,font-style," +
      "text-decoration,margin,margin-left,margin-right,margin-top," +
      "margin-bottom,width,border,vertical-align,page-break-before",
  },
  // Keep &nbsp; as &nbsp; rather than re-encoding it: multi-space gaps (the
  // "Dispatch No. ... Date :" line) reach the editor as non-breaking spaces,
  // and TinyMCE's default entity handling mangles them.
  entity_encoding: "raw",
  // Strip Word/Google Docs cruft on paste - admins will paste from Word.
  paste_webkit_styles: "none",
  paste_remove_styles_if_webkit: true,
  content_style: noticeBodyCss("body"),
});

/**
 * True when the editor holds nothing but empty markup, so we can block a save
 * that would wipe the notice body.
 */
export const isHtmlBlank = (html) => {
  if (!html) return true;
  const text = String(html)
    .replace(/<br\s*\/?>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]*>/g, "")
    .trim();
  return text.length === 0;
};
